// stores/navigationStore.js
import logger from "../utils/logger";
import { get } from "svelte/store";
import { hashtagDropdownVisible } from "../extensions/Hashtag";

export function useNavigation() {
  // Check if caret is at the start of element
  function isCaretAtStart(element) {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);
    if (range.collapsed === false) return false;

    const startContainer = range.startContainer;

    // If we're in a text node, check if we're at the beginning of the text
    if (startContainer.nodeType === Node.TEXT_NODE) {
      return (
        range.startOffset === 0 &&
        (startContainer.previousSibling === null ||
          (startContainer.parentNode && startContainer.parentNode === element))
      );
    }

    // If we're in an element, check if we're at the first position
    return range.startOffset === 0;
  }

  // Check if caret is at the end of element
  function isCaretAtEnd(element) {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);
    if (range.collapsed === false) return false;

    const startContainer = range.startContainer;

    // If we're in a text node, check if we're at the end of the text
    if (startContainer.nodeType === Node.TEXT_NODE) {
      return (
        range.startOffset === startContainer.length &&
        (startContainer.nextSibling === null ||
          (startContainer.parentNode && startContainer.parentNode === element))
      );
    }

    // If we're in an element, check if we're at the last position
    return range.startOffset === startContainer.childNodes.length;
  }

  // Helper to move the caret within a block
  function moveCaret(element, position = "start") {
    logger.debug("Moving caret to", position);
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(element);
    range.collapse(position === "start");
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // Handle arrow navigation between blocks
  function handleArrowNavigation(allEditors, currentIndex, direction) {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    // Capture initial caret position
    const initialCaretOffset = range.startOffset;
    const initialCaretNode = range.startContainer;

    setTimeout(() => {
      // Check caret position after the event
      const newSelection = window.getSelection();
      if (newSelection.rangeCount === 0) return;

      const newRange = newSelection.getRangeAt(0);
      const newCaretOffset = newRange.startOffset;
      const newCaretNode = newRange.startContainer;

      const caretMoved =
        initialCaretOffset !== newCaretOffset ||
        initialCaretNode !== newCaretNode;

      if (!caretMoved) {
        if (direction === "end" && currentIndex < allEditors.length - 1) {
          const nextEditor = allEditors[currentIndex + 1];
          nextEditor.focus();
          moveCaret(nextEditor, "start");
        } else if (direction === "start" && currentIndex > 0) {
          const prevEditor = allEditors[currentIndex - 1];
          prevEditor.focus();
          moveCaret(prevEditor, "end");
        }
      }
    }, 0);
  }

  function handleKeyNavigation({
    event,
    editor,
    blockId,
    isFirstBlock,
    isEmptyBlock,
  }) {
    // Check if hashtag dropdown is visible
    const isHashtagDropdownOpen = get(hashtagDropdownVisible);

    // If hashtag dropdown is open, don't handle Up/Down navigation
    if (
      isHashtagDropdownOpen &&
      (event.key === "ArrowUp" || event.key === "ArrowDown")
    ) {
      // Let the hashtag dropdown handle these keys
      return { action: "none" };
    }

    const editorContainer = editor.closest("#editor-container");
    if (!editorContainer) return { action: "none" };

    const allEditors = Array.from(
      editorContainer.querySelectorAll("[contenteditable=true]"),
    );
    const currentIndex = allEditors.indexOf(
      editor.querySelector("[contenteditable=true]"),
    );

    if (
      event.shiftKey &&
      (event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight")
    ) {
      return { action: "none" };
    }

    let result = { action: "none" };

    switch (event.key) {
      case "Enter":
        // Don't create new blocks if hashtag dropdown is open
        if (isHashtagDropdownOpen) {
          return { action: "none" };
        }

        if (event.shiftKey) {
          event.preventDefault();
          result = {
            action: "createBlock",
            index: currentIndex,
          };

          // Clear selection in current block to avoid focus jumping back
          window.getSelection().removeAllRanges();
        }
        break;

      case "Backspace":
        if (isEmptyBlock) {
          // Block is empty, attempt to delete it if we have more than one
          if (allEditors.length > 1) {
            event.preventDefault();

            // Focus the previous block before deleting
            if (currentIndex > 0) {
              const prevEditor = allEditors[currentIndex - 1];
              prevEditor.focus();
              moveCaret(prevEditor, "end");
            } else if (currentIndex == 0) {
              const nextEditor = allEditors[currentIndex + 1];
              nextEditor.focus();
            }

            // Signal to delete the block
            result = { action: "deleteBlock" };
          }
        }
        break;

      case "ArrowDown":
        if (isHashtagDropdownOpen) {
          // Let the hashtag dropdown handle this
          return { action: "none" };
        }

        if (currentIndex === 0 && isEmptyBlock) {
          result = { action: "deleteBlock" };
          handleArrowNavigation(allEditors, currentIndex, "end");
        } else if (currentIndex === allEditors.length - 1 && !isEmptyBlock) {
          event.preventDefault();
          result = { action: "createBlock", index: currentIndex };
        } else {
          handleArrowNavigation(allEditors, currentIndex, "end");
        }
        break;

      case "ArrowUp":
        if (isHashtagDropdownOpen) {
          // Let the hashtag dropdown handle this
          return { action: "none" };
        }

        // Special handling for the top block
        if (
          isFirstBlock &&
          !isCaretAtStart(editor.querySelector("[contenteditable=true]"))
        ) {
          // Normal navigation within the block, let the default behavior work
          break;
        } else if (
          isFirstBlock &&
          isCaretAtStart(editor.querySelector("[contenteditable=true]")) &&
          !isEmptyBlock
        ) {
          // First block is not empty and caret at start - create a new block at the top
          event.preventDefault();
          result = { action: "createBlockAtTop" };
          break;
        } else {
          // Standard navigation between blocks
          handleArrowNavigation(allEditors, currentIndex, "start");
        }
        break;

      case "ArrowRight":
        if (isCaretAtEnd(editor.querySelector("[contenteditable=true]"))) {
          event.preventDefault();
          if (currentIndex < allEditors.length - 1) {
            const nextEditor = allEditors[currentIndex + 1];
            nextEditor.focus();
            moveCaret(nextEditor, "start");
          }
        }
        break;

      case "ArrowLeft":
        if (isCaretAtStart(editor.querySelector("[contenteditable=true]"))) {
          event.preventDefault();
          if (currentIndex > 0) {
            const prevEditor = allEditors[currentIndex - 1];
            prevEditor.focus();
            moveCaret(prevEditor, "end");
          }
        }
        break;
    }

    return result;
  }

  return {
    isCaretAtStart,
    isCaretAtEnd,
    moveCaret,
    handleArrowNavigation,
    handleKeyNavigation,
  };
}
